import { Navigate } from 'react-router-dom'
import { ReactNode, useRef, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { NivelAcesso } from '../types/auth'
import { CircularProgress, Box } from '@mui/material'
import { hasAuthTokens, clearInvalidTokens } from '../utils/authHelpers'

interface ProtectedRouteProps {
  children: ReactNode
  requiredLevel?: NivelAcesso
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredLevel,
}) => {
  const { isAuthenticated, isLoading, hasPermission, user } = useAuth()
  const hasClearedTokens = useRef(false)  // Ref para evitar limpar tokens múltiplas vezes
  const clearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const contextRedirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // Limpar timers anteriores
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current)
      clearTimeoutRef.current = null
    }
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current)
      resetTimeoutRef.current = null
    }

    // Se não autenticado e ainda há tokens, limpar com delay (uma vez)
    if ((!isAuthenticated || !user) && hasAuthTokens() && !hasClearedTokens.current) {
      hasClearedTokens.current = true
      clearTimeoutRef.current = setTimeout(() => {
        if (hasAuthTokens()) {
          clearInvalidTokens()
        }
        resetTimeoutRef.current = setTimeout(() => {
          hasClearedTokens.current = false
        }, 2000)
      }, 2000)
    }

    return () => {
      if (clearTimeoutRef.current) {
        clearTimeout(clearTimeoutRef.current)
      }
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current)
      }
      if (contextRedirectTimeoutRef.current) {
        clearTimeout(contextRedirectTimeoutRef.current)
      }
    }
  }, [isAuthenticated, user])

  // CRÍTICO: Se está carregando, mostrar loading
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  // CRÍTICO: Se não está autenticado (user é null), bloquear acesso
  if (!isAuthenticated || !user) {
    // Sempre redirecionar para login se não estiver autenticado
    return <Navigate to="/login" replace />
  }
  
  // Se chegou aqui, está autenticado - resetar flag
  useEffect(() => {
    hasClearedTokens.current = false
  }, [isAuthenticated])

  // No admin portal, o contexto é sempre global (sem empresa).
  // Inicializamos o contexto automaticamente se estiver ausente para evitar redirecionamento para seleção de empresa.
  let userContextStr = localStorage.getItem('user_context')
  if (!userContextStr && user) {
    let role: 'SUPER_ADMIN' | 'ADMINISTRADOR' | 'USUARIO' = 'USUARIO'
    let nivel_acesso_efetivo = NivelAcesso.USUARIO_PADRAO
    
    if (user.nivel_acesso === NivelAcesso.SUPER_ADMIN) {
      role = 'SUPER_ADMIN'
      nivel_acesso_efetivo = NivelAcesso.SUPER_ADMIN
    } else if (user.nivel_acesso === NivelAcesso.ADMINISTRADOR) {
      role = 'ADMINISTRADOR'
      nivel_acesso_efetivo = NivelAcesso.ADMINISTRADOR
    }
    
    const defaultContext = {
      role,
      empresa_id: null,
      nivel_acesso_efetivo,
      preferencias: {},
      empresa_nome: null,
      empresa_razao_social: null,
      empresa_cnpj: null
    }
    
    localStorage.setItem('user_context', JSON.stringify(defaultContext))
    userContextStr = JSON.stringify(defaultContext)
  }

  if (requiredLevel !== undefined) {
    // Verificar permissão considerando o contexto escolhido
    if (userContextStr) {
      try {
        const context = JSON.parse(userContextStr)
        // Se o contexto tem nivel_acesso_efetivo, usar ele
        if (context.nivel_acesso_efetivo !== undefined) {
          if (context.nivel_acesso_efetivo > requiredLevel) {
            return <Navigate to="/" replace />
          }
        } else {
          // Fallback para verificação normal
          if (!hasPermission(requiredLevel)) {
            return <Navigate to="/" replace />
          }
        }
      } catch (e) {
        // Se der erro ao parsear, usar verificação normal
        if (!hasPermission(requiredLevel)) {
          return <Navigate to="/" replace />
        }
      }
    } else {
      if (!hasPermission(requiredLevel)) {
        return <Navigate to="/" replace />
      }
    }
  }

  return <>{children}</>
}

export default ProtectedRoute


